import type { ImageWidget } from "~/types/widgets"
import { Picture, Source } from "~/components/ui/Picture"
import { isSchedulingActive, type Scheduling } from "~/sdk/contentScheduling"
import ImageComponent from "~/components/ui/Image"

interface ImageProps {
    /** @title Imagem */
    src: ImageWidget
    /** @title Largura (em px) */
    width?: number
    /** @title Altura (em px) */
    height?: number
}

export interface Props {
    /**
     * @title Imagem desktop
     * @description Será exibida em telas com largura superior a 1024px
     */
    desktop?: ImageProps
    /**
     * @title Imagem mobile
     * @description Será exibida em telas com largura inferior ou igual a 1024px
     */
    mobile?: ImageProps
    /** @title Texto alternativo */
    alt?: string
    /** @title Link */
    href?: string
    /** @title Deixe desmarcado se você precisa scrollar para a imagem aparecer na tela */
    lazy?: boolean
    /** @title Limitar à largura do container (1810px) */
    maxWidth?: boolean
    /**
     * @title Agendamento
     * @description Deixe este campo vazio para manter sempre visível
     */
    scheduling?: Scheduling
}

/**
 * Aspect (altura/largura) das dimensões salvas no CMS, ou `undefined` quando o
 * bloco não tem nenhuma — o caso da MAIORIA (193 das 282 instâncias de
 * `Images/Image` em `.deco/blocks` só têm `src`).
 *
 * NÃO devolva palpite aqui. O `height` que chega no `<Source>`/`<Image>` vai
 * junto no `getSrcSet` com `fit=cover`, então o CDN CROPA a imagem para a caixa
 * pedida: um palpite de fileta 10:1 num banner 3:1 corta e amplia a arte (foi
 * a regressão do banner principal da home no PR #214). Só o `LoadingFallback`,
 * que não tem imagem nenhuma, pode chutar.
 */
export function bannerAspect(desktop?: ImageProps, mobile?: ImageProps) {
    if (desktop?.width && desktop?.height) return desktop.height / desktop.width
    if (mobile?.width && mobile?.height) return mobile.height / mobile.width
    return undefined
}

export default function ResponsiveImage(
    { desktop, mobile, alt, href, lazy, maxWidth = false, scheduling }: Props,
) {
    const isActive = isSchedulingActive(scheduling)
    if ((!desktop && !mobile) || !isActive) return null

    const fallbackSrc = mobile?.src ?? desktop?.src ?? ""
    if (!fallbackSrc) return null

    // Largura tem default (a original também usava 1920/1024) porque o CDN
    // precisa de uma para redimensionar. Altura NÃO: `height` entra na URL do
    // CDN com `fit=cover` e cropa. Sem ele o CDN só reduz pela largura e
    // preserva o aspect nativo do arquivo — comportamento do site original.
    //
    // Também não cruzamos os aspects entre as variantes: a arte mobile é outra
    // imagem (quase sempre mais alta), então derivar a altura do mobile pelo
    // aspect do desktop cropa igual.
    const desktopWidth = desktop?.width ?? 1920
    const mobileWidth = mobile?.width ?? 1024
    const desktopHeight = desktop?.height
    const mobileHeight = mobile?.height

    const body = (
        <Picture>
            <Source
                media="(max-width: 1024px)"
                src={mobile?.src ?? desktop?.src ?? ""}
                width={mobileWidth}
                height={mobileHeight}
            />
            <Source
                media="(min-width: 1025px)"
                src={desktop?.src ?? mobile?.src ?? ""}
                width={desktopWidth}
                height={desktopHeight}
            />
            <ImageComponent
                className={`w-full ${!href && maxWidth ? "max-w-[1810px] mx-auto" : ""}`}
                src={fallbackSrc}
                // `alt={undefined}` faz o React OMITIR o atributo, e `<img>` sem
                // `alt` é erro. `alt=""` é a forma correta de marcar decorativa.
                alt={alt ?? ""}
                loading={lazy ? "lazy" : undefined}
                width={desktopWidth}
                height={desktopHeight}
            />
        </Picture>
    )

    if (href) {
        // O único filho é a <img> decorativa, então sem `alt` o link ficaria com
        // ZERO nome acessível (`link-name`). Deriva do href — string simples de
        // propósito, `new URL` crasharia com href relativo.
        const linkLabel = alt ||
            `Ir para ${href.replace(/^https?:\/\//, "").replace(/\/+$/, "")}`

        return (
            <a
                className={maxWidth ? "w-full max-w-[1810px] mx-auto" : ""}
                href={href}
                aria-label={linkLabel}
            >
                {body}
            </a>
        )
    }

    return body
}
